import { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "./authentication.provider";
import { axiosInstance } from "../api/axiosInstance";
import { useApi } from "../hooks/api.hook";
import { isAxiosError } from "axios";
import { SignInToCheckoutComponent } from "../components/signInToCheckout";
import { CartStorageUtil } from "../utils/cart-util";
import { useLocation } from "react-router";
import { PageLoader } from "../components/loaders";
import { Mixpanel } from "../utils/mixpanel";

export const CartContext = createContext({});

export const CartProvider = ({ children }) => {
  const location = useLocation();
  const [loadingCart, setLoadingCart] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const { isAuthenticated, isGuestUser, user } = useAuth();
  const api = useApi();
  const [cartItems, setCartItems] = useState(() =>
      CartStorageUtil.getGuestCart()
  );

  useEffect(() => {
    setShowLoginModal(false);
  }, [location.state]);

  useEffect(() => {
    if (!isAuthenticated && isGuestUser) {
      CartStorageUtil.setGuestCart(cartItems);
    }
  }, [cartItems, isAuthenticated, isGuestUser]);

  useEffect(() => {
    const fetchUserCart = async () => {
      setLoadingCart(true);
      try {
        if (isAuthenticated) {
          const { data } = await api.get("/cart");
          setCartItems(CartStorageUtil.normalizeCartResponse(data.items || []));
        } else {
          const guestCart = CartStorageUtil.getGuestCart();
          setCartItems(guestCart);

          if (guestCart.length > 0) {
            await validateGuestCartItems();
          }
        }
      } catch {
      } finally {
        setLoadingCart(false);
      }
    };
    fetchUserCart();
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) syncLocalCartToServer();
  }, [isAuthenticated]);

  const makeApiRequest = async (apiCall, runOnClose) => {
    try {
      const result = await apiCall();
      const data = result.data;
      return {
        success: data.status || false,
        data,
        message:
            typeof data.message === "object" ? data.message[0] : data.message,
      };
    } catch (err) {
      return {
        success: false,
        data: isAxiosError(err) ? err.response?.data : undefined,
        message:
            err.response?.data?.message || err.message || "Something went wrong",
      };
    } finally {
      runOnClose?.();
    }
  };

  const addToCart = async (item, runOnClose, page) => {
    if (actionLoading) return;
    setActionLoading(true);

    try {
      const id = item.split_buy_id;

      if (isAuthenticated) {
        const response = await makeApiRequest(
            () =>
                api.post("/cart", {
                  splitBuy: id,
                  cartType: "split_buy",
                }),
            runOnClose
        );

        if (response.success) {
          const { data } = await api.get("/cart");
          setCartItems(CartStorageUtil.normalizeCartResponse(data.items || []));
          Mixpanel.track_home_add_to_cart({
            split_buy_id: item.split_buy_id,
            split_buy_name: item?.title ?? item?.split_buy_slog,
            screen: page,
          });
          return response;
        } else {
          if (response.message?.includes("already added")) {
            await removeFromCart(findCartItemIdBySplitBuyId(id));
            Mixpanel.track_home_remove_from_cart({
              split_buy_id: item.split_buy_id,
              split_buy_name: item?.title ?? item?.split_buy_slog,
              screen: page,
            });
            return { success: true, message: "Item removed from cart" };
          }
          return response;
        }
      } else {
        const existsIndex = cartItems.findIndex((i) => i.split_buy_id === id);
        if (existsIndex >= 0) {
          const updated = cartItems.filter((_, index) => index !== existsIndex);
          setCartItems(updated);
          CartStorageUtil.setGuestCart(updated);
          Mixpanel.track_home_remove_from_cart({
            split_buy_id: item.split_buy_id,
            split_buy_name: item?.title ?? item?.split_buy_slog,
            screen: page,
          });
          return { success: true, message: "Item removed from cart" };
        } else {
          const updated = [...cartItems, item];
          setCartItems(updated);
          CartStorageUtil.setGuestCart(updated);
          Mixpanel.track_home_add_to_cart({
            split_buy_id: item.split_buy_id,
            split_buy_name: item?.title ?? item?.split_buy_slog,
            screen: page,
          });
          return { success: true, message: "Added to cart" };
        }
      }
    } finally {
      setActionLoading(false);
      runOnClose?.();
    }
  };

  const leaveSplitGroup = async (splitBuyId, runOnClose) => {
    if (actionLoading && !isAuthenticated) return;
    setActionLoading(true);

    try {
      if (isAuthenticated) {
        return await makeApiRequest(() =>
            api.patch(`/split-buy/leave-split-buy/${splitBuyId}`)
        );
      }
    } finally {
      setActionLoading(false);
      runOnClose?.();
    }
  };

  const findCartItemIdBySplitBuyId = (splitBuyId) => {
    const item = cartItems.find((i) => i.split_buy_id === splitBuyId);
    return item?._id;
  };

  const removeFromCart = async (itemId) => {
    setActionLoading(true);
    try {
      const updated = cartItems.filter((i) => i._id !== itemId);
      if (isAuthenticated) {
        await api.delete(`/cart/${itemId}`);
      } else {
        CartStorageUtil.setGuestCart(updated);
      }
      setCartItems(updated);
    } finally {
      setActionLoading(false);
    }
  };

  const clearCart = () => {
    setCartItems([]);
    CartStorageUtil.clearGuestCart();
  };

  const syncLocalCartToServer = async () => {
    const localCart = CartStorageUtil.getGuestCart();
    if (!localCart.length) return;

    try {
      await Promise.allSettled(
          localCart.map((item) =>
              makeApiRequest(() =>
                  api.post("/cart", {
                    splitBuy: item.split_buy_id,
                    cartType: "split_buy",
                  })
              )
          )
      );

      CartStorageUtil.clearGuestCart();
      CartStorageUtil.clearGuestId();
    } catch {
      // Handle error silently
    }
  };

  const handleCheckout = async (
      split_buy,
      addressIdOverride = null,
      discount_code = undefined,
      runOnClose
  ) => {
    sessionStorage.setItem(
        "pending_checkout",
        JSON.stringify({
          split_buy,
          addressIdOverride,
          discount_code,
          timestamp: Date.now(),
          redirectTo: "/cart/checkout",
        })
    );

    if (!isAuthenticated && !isGuestUser) {
      setShowLoginModal(true);
      return {
        success: false,
        message: "Authentication required",
        requiresAuth: true,
      };
    }

    try {
      setActionLoading(true);

      const filterIncompatibleItems = (splitBuys, userState) => {
        const stateAliases = {
          Abuja: "Abuja",
          FCT: "Abuja",
          "Federal Capital Territory": "Abuja",
          "Federal Capital Territory (FCT)": "Abuja",
        };
        const normalizeState = stateAliases[userState] ?? userState;

        const incompatibleItems = [];
        const compatibleSplitBuys = [];
        let itemNames = "";
        let requiredStates = "";

        for (const sb of splitBuys) {
          const cartItem = cartItems.find(
              (item) => item.split_buy_id === sb.id
          );

          if (cartItem) {
            const cartLocation = cartItem.location;

            if (
                cartLocation &&
                normalizeState &&
                !cartLocation.includes(normalizeState)
            ) {
              incompatibleItems.push({
                id: cartItem._id,
                name: cartItem.title || "Unknown item",
                cartLocation: cartLocation,
                userState: normalizeState,
              });
            } else {
              compatibleSplitBuys.push(sb);
            }
          } else {
            compatibleSplitBuys.push(sb);
          }
        }

        if (incompatibleItems.length > 0) {
          itemNames = incompatibleItems.map((item) => item.name).join(", ");
          requiredStates = [
            ...new Set(
                incompatibleItems.map((item) => {
                  const locationParts = item.cartLocation.split(", ");
                  return locationParts[locationParts.length - 1];
                })
            ),
          ].join(", ");
        }

        return {
          incompatibleItems,
          compatibleSplitBuys,
          itemNames,
          requiredStates,
        };
      };

      if (isAuthenticated) {
        let selectedAddress = null;

        if (addressIdOverride) {
          const { data: addressData } = await api.get(
              `/address/${addressIdOverride}`
          );
          selectedAddress = addressData?.address || addressData;
        } else {
          const { data: addressResponse } = await api.get("/address");
          const addresses = addressResponse?.addresses || [];
          selectedAddress = addresses[0];
        }

        if (!selectedAddress) {
          setActionLoading(false);
          return {
            success: false,
            message: "Please add a delivery address before checkout.",
          };
        }

        const {
          incompatibleItems,
          compatibleSplitBuys,
          itemNames,
          requiredStates
        } = filterIncompatibleItems(split_buy, selectedAddress.state);

        if (incompatibleItems.length > 0) {
          split_buy = compatibleSplitBuys;

          if (split_buy.length === 0) {
            return {
              success: false,
              message:
                  "No items available for delivery to your selected address.",
            };
          }
        }

        const addressId = selectedAddress._id;
        const { data: checkoutData } = await api.post("/cart/combine", {
          split_buy,
          address_id: addressId,
          discount_code,
        });

        return {
          success: checkoutData.status || false,
          data: checkoutData,
          message:
              incompatibleItems.length > 0
                  ? `Buyer's delivery location state (${selectedAddress.state}) is not in the allowed delivery locations (${requiredStates}) for ${itemNames}. The incompatible items have been removed from your cart.`
                  : typeof checkoutData.message === "object"
                      ? checkoutData.message[0]
                      : checkoutData.message,
        };
      } else {
        const {
          incompatibleItems,
          compatibleSplitBuys,
          itemNames,
          requiredStates,
        } = filterIncompatibleItems(split_buy, user?.state);

        if (incompatibleItems.length > 0) {
          split_buy = compatibleSplitBuys;

          if (split_buy.length === 0) {
            return {
              success: false,
              message:
                  "No items available for delivery to your selected address.",
            };
          }
        }

        const { data: checkoutData } = await axiosInstance.post(
            "/cart/combine",
            {
              split_buy,
              guest_id: user?._id,
              discount_code,
            },
            {
              headers: {
                "guest-user-key": process.env.REACT_APP_API_GUEST_USER_KEY,
              },
            }
        );

        return {
          success: checkoutData.status || false,
          data: checkoutData,
          message:
              incompatibleItems.length > 0
                  ? `Buyer's delivery location state (${user?.state}) is not in the allowed delivery locations (${requiredStates}) for ${itemNames}. The incompatible items have been removed from your cart.`
                  : typeof checkoutData.message === "object"
                      ? checkoutData.message[0]
                      : checkoutData.message,
        };
      }
    } catch (err) {
      setActionLoading(false);
      return {
        success: false,
        data: isAxiosError(err) ? err.response?.data : undefined,
        message:
            err.response?.data?.message ||
            err.message ||
            "Checkout failed. Try again.",
      };
    } finally {
      setActionLoading(false);
      runOnClose?.();
    }
  };

  const handlePendingCheckout = async () => {
    const pendingCheckout = sessionStorage.getItem("pending_checkout");
    if (!pendingCheckout) return null;

    try {
      setActionLoading(true);
      const { split_buy, addressIdOverride, discount_code } =
          JSON.parse(pendingCheckout);

      return await handleCheckout(split_buy, addressIdOverride, discount_code);
    } catch (error) {
      return null;
    } finally {
      setActionLoading(false);
    }
  };

  const removeGuestCartItems = (itemIds) => {
    const updated = cartItems.filter((i) => !itemIds.includes(i._id));
    setCartItems(updated);
    CartStorageUtil.setGuestCart(updated);
  };

  const toggleCheckout = () => setShowLoginModal((prev) => !prev);

  const updateCartItemQuantity = async (cartItemId, newQuantity) => {
    if (isAuthenticated) return;
    setActionLoading(true);
    try {
      const guestCart = CartStorageUtil.getGuestCart();
      const updatedCart = guestCart.map((item) =>
          item._id === cartItemId
              ? {
                ...item,
                slot: newQuantity,
                quantity: newQuantity,
              }
              : item
      );

      setCartItems(updatedCart);
      CartStorageUtil.setGuestCart(updatedCart);
      return { success: true, message: "Quantity updated" };
    } finally {
      setActionLoading(false);
    }
  };

  const fetchCartItemDetails = async (id, runOnClose) => {
    try {
      const { data } = await api.get(`/split-buy/details?slug=${id}`);
      return {
        success: data.status || false,
        data: data,
        message:
            typeof data.message === "object" ? data.message[0] : data.message,
      };
    } catch (err) {
      return {
        success: false,
        data: isAxiosError(err) ? err.response?.data : undefined,
        message:
            err.response?.data?.message ||
            err.message ||
            "Checkout failed. Try again.",
      };
    } finally {
      runOnClose?.();
    }
  };

  const validateGuestCartItems = async () => {
    if (isAuthenticated || !cartItems.length) return;

    try {
      const validationPromises = cartItems.map(async (item) => {
        try {
          const { data } = await axiosInstance.get(
              `/split-buy/details?slug=${item.split_buy_slug}`
          );

          if (data.status && data.data) {
            const splitBuy = data.data;
            const availableSlots = splitBuy.slots_left;
            const buyInAmount = splitBuy.split_cost_per_slot;
            const isSplitMember = splitBuy.is_split_member;
            const isAvailable =
                splitBuy.split_buy_status !== "completed" &&
                availableSlots > 0 &&
                !isSplitMember;

            if (isAvailable) {
              const maxAllowedQuantity = Math.min(
                  item.quantity,
                  availableSlots
              );

              const updatedItem = {
                ...item,
                available_slots: availableSlots,
                price: buyInAmount,
                title: splitBuy.product?.title || item.title,
                location:
                    splitBuy.split_buy_product_location?.string ||
                    splitBuy.choice_of_delivery_locations?.[0] ||
                    item.location,
                delivery_date: splitBuy.delivery_date,
                join_period_end_date: splitBuy.join_period_end_date,
                number_of_buy_ins_attained: splitBuy.number_of_buy_ins_attained,
                quantity: maxAllowedQuantity,
                slot: maxAllowedQuantity,
                split_buy_status: splitBuy.split_buy_status,
                total_slots: splitBuy.number_of_buy_ins_required,
                last_validated: new Date().toISOString(),
              };

              return {
                type: "validated",
                item: updatedItem,
                wasAdjusted: maxAllowedQuantity < item.quantity,
                adjustment:
                    maxAllowedQuantity < item.quantity
                        ? {
                          name: splitBuy.product?.title || item.title,
                          original: item.quantity,
                          new: maxAllowedQuantity,
                        }
                        : null,
              };
            } else {
              return {
                type: "removed",
                name:
                    splitBuy.product?.title || item.title || item.split_buy_slug,
              };
            }
          } else {
            return {
              type: "removed",
              name: item.title || item.split_buy_slug,
            };
          }
        } catch (error) {
          return {
            type: "error",
            item: {
              ...item,
              last_validated: new Date().toISOString(),
              validation_error: true,
            },
          };
        }
      });

      const results = await Promise.all(validationPromises);

      const validatedItems = [];
      const removedItems = [];
      const updatedItems = [];

      results.forEach((result) => {
        switch (result.type) {
          case "validated":
            validatedItems.push(result.item);
            if (result.wasAdjusted && result.adjustment) {
              updatedItems.push(result.adjustment);
            }
            break;
          case "removed":
            removedItems.push(result.name);
            break;
          case "error":
            validatedItems.push(result.item);
            break;
        }
      });

      if (
          validatedItems.length !== cartItems.length ||
          updatedItems.length > 0
      ) {
        setCartItems(validatedItems);
        CartStorageUtil.setGuestCart(validatedItems);
      }

      return validatedItems;
    } catch (error) {
      return cartItems;
    }
  };

  return (
      <CartContext.Provider
          value={{
            cartItems,
            addToCart,
            removeFromCart,
            clearCart,
            syncLocalCartToServer,
            handleCheckout,
            showLoginModal,
            loadingCart,
            actionLoading,
            setShowLoginModal,
            removeGuestCartItems,
            updateCartItemQuantity,
            fetchCartItemDetails,
            validateGuestCartItems,
            handlePendingCheckout,
            leaveSplitGroup
          }}
      >
        <SignInToCheckoutComponent
            toggle={toggleCheckout}
            isOpen={showLoginModal}
        />
        {children}
        {actionLoading && (
            <div
                role="alert"
                aria-busy="true"
                className="!tw-z-[100000] position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
                style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
            >
              <PageLoader />
            </div>
        )}
      </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);